<template>
	<div>
		<div class="search-wrapper" v-if="isSearchPage">
			<div class="search-heading">
				<i18n-t keypath="search_results_summary" tag="div" v-if="query">
					<template #Term
						><span class="search-query" v-text="query" data-private
					/></template>
				</i18n-t>
				<div
					v-if="!query"
					v-text="
						hasSearchCriteria
							? $t('Search Results')
							: $t('search_configure_filters_label')
					"
				/>
				<CloseIcon
					class="pointer close-icon"
					@click="closeSearch"
					:aria-label="$t('common_button_close')"
					tabindex="0"
					role="button"
				/>
			</div>

			<div class="search-filters">
				<div class="search-filters-right">
					<SortDropdown v-model="sortOrder" />
					<ChatFilter v-model="chats" @open="onFilterOpen" />
					<FromFilter v-model="users" @open="onFilterOpen" />
					<DateFilter
						:startDate="start_date"
						:endDate="end_date"
						:relativeDate="relative_date_duration"
						@update="dateFilterUpdate"
					/>
				</div>

				<Button
					v-if="hasFilters"
					link
					class="clear-filters"
					@click="clearFilters"
					>{{ $t('search_clear_all_filters') }}</Button
				>
				<div class="clear-filters-placeholder" v-else></div>
			</div>

			<Tabs
				v-show="hasSearchCriteria"
				:tabs="tabs"
				:alignLeft="true"
				:selectedBottom="true"
				:defaultSelected="selectedTab.id"
				@change="onTabChange"
			>
				<template v-slot="{ tab }">
					<div
						v-text="
							tabHasCount(tab)
								? $t(`search_${tab.path}_count`, [getResults(tab.path).count])
								: $t(`search_${tab.path}_label`)
						"
					/>
				</template>
			</Tabs>

			<div class="search-error" v-if="showError && !isLoading">
				<div class="search-error-icon"></div>
				<div>{{ $t('search_error_label') }}</div>
				<a class="search-anchor" @click="tryAgain">
					{{ $t('search_error_retry_button') }}
				</a>
			</div>

			<div class="search-loading" v-if="isLoading && !results.results.length">
				<div>{{ $t('search_loading') }}</div>
				<SpinnerOverlay />
			</div>

			<div
				class="search-no-results"
				v-if="
					!isLoading &&
					!showError &&
					!results.results.length &&
					hasSearchCriteria
				"
			>
				<div class="no-results-icon"></div>
				<div
					v-if="query"
					v-text="
						$t(`search_no_results_${searchType}_label_with_value`, {
							Term: query,
						})
					"
				/>
				<div
					v-if="!query"
					v-text="$t(`search_no_results_${searchType}_label`)"
				/>
				<a
					v-for="result in otherResults"
					:key="result.type"
					class="search-anchor"
					@click="goToTab(result.type)"
					v-text="$t(`search_no_results_${result.type}_button`, [result.count])"
				/>
			</div>

			<SearchList
				v-if="results.results.length > 0"
				:results="results.results"
				:hasMore="!results.isComplete"
				@load-more="loadNextPage"
				ref="list"
			>
				<template v-slot="{ result }">
					<MessageResult
						:result="result"
						:isLoading="loadingMessageId === result.message.id"
						v-if="searchType === types.MESSAGES"
					/>
					<FileResult
						:result="result"
						v-if="searchType === types.FILES"
						:isLoading="loadingMessageId === result.message.id"
					/>
				</template>
			</SearchList>
		</div>
	</div>
</template>

<script>
import SearchList from './SearchList'
import { CloseIcon } from '@/icons'
import { Tabs, Button } from '@/components/common'
import { searchMessages } from '@/api/message'
import { getCancelToken, isCanceled } from '../../helpers/api'
import SpinnerOverlay from '@/components/SpinnerOverlay'
import SortDropdown from './SortDropdown'
import { sortTypes } from '@/helpers/constants'
import MessageResult from './MessageResult'
import FileResult from './FileResult'
import {
	normalizeMessage,
	getOrganization,
	getChat,
	getClientMeta,
} from '@/store'
import { addEscapeListener, removeEscapeListener } from '@/helpers/escape'
import { fetchMyMembershipForChat } from '@/api/chat'
import ChatFilter from './ChatFilter'
import FromFilter from './FromFilter'
import DateFilter from './DateFilter'
import { saveRecentSearch, setSearchQueryParams } from './searchUtils'
import isEqual from 'lodash/isEqual'
import moment from 'moment-timezone'
import { redirectToLastActiveGroup } from '@/helperMethods'
import debounce from 'debounce'

const PAGE_SIZE = 25

export const types = {
	MESSAGES: 'messages',
	FILES: 'files',
}

const tabs = [
	{ id: 1, path: 'messages' },
	{ id: 2, path: 'files' },
]

const emptyState = {
	results: [],
	count: -1,
	isComplete: false,
}

export default {
	name: 'Search',

	props: {
		// props will come from router
		searchType: { type: String, default: types.MESSAGES },
		query: { type: String, default: null },
		orderby: { type: String, default: sortTypes.NEWEST },
		buster: { type: [Number, String], default: null },
		user_ids: { type: Array, default: null },
		bubble_ids: { type: Array, default: null },
		start_date: { type: String, default: null },
		end_date: { type: String, default: null },
		relative_date_duration: { type: String, default: '' },
	},

	components: {
		CloseIcon,
		SearchList,
		Tabs,
		SortDropdown,
		MessageResult,
		FileResult,
		SpinnerOverlay,
		Button,
		ChatFilter,
		FromFilter,
		DateFilter,
	},

	data() {
		return {
			escapeId: null,
			tabs,
			resultsByType: {},
			isLoading: false,
			types,
			scrollPosition: null,
			showError: false,
			isSearchPage: true,
			loadingMessageId: null,
			recentSearches: [],
			chats: this.bubble_ids,
			users: this.user_ids,
			dates: {},
			sortOrder: sortTypes.NEWEST,
		}
	},

	created() {
		this.fetchSearchResultsDebounced = debounce(this.fetchSearchResults, 250)

		this.removeAfterEachHook = this.$router.afterEach((to, from) => {
			if (to.name === 'search' && from.name !== 'search') {
				// if coming to search fresh, we need to preserve the from route
				// so we can return there if the user clicks 'X' or hits esc
				if (!this.backToRoute) {
					this.backToRoute = Object.assign({}, from)
				}
			}
		})
	},

	mounted() {
		this.recentSearches = getClientMeta('recent_searches') || []
		this.cancelTokensByType = {}
		this.fetchSearchResults()

		this.escapeId = addEscapeListener(() => this.onClickClose())

		this.$onEventBus('clearSearchQuery', () => {
			this.clearResults()
		})
	},

	beforeUnmount() {
		this.removeAfterEachHook?.()
		removeEscapeListener(this.escapeId)
	},

	activated() {
		if (this.isEmpty) {
			// user previously closed search and cleared results so we need to refetch
			this.fetchSearchResults()
		}
		// KeepAlive has reactivated our component
		// reregister the esc listener at the top of the esc stack
		this.escapeId = addEscapeListener(() => this.onClickClose())

		// if results were cleaned up for whatever reason, try refetching latest
		if (this.scrollPosition) {
			const position = this.scrollPosition
			this.scrollPosition = null
			this.$nextTick(() => {
				if (this.$refs.list) {
					// put the user back at the location they previously were
					this.$refs.list.$el.scrollTop = position
				}
			})
		}
	},

	deactivated() {
		// KeepAlive has deactivated our component
		removeEscapeListener(this.escapeId)
	},

	watch: {
		query(q) {
			if (this.results.query === q) return
			this.resultsByType = {}
			this.fetchSearchResults()
		},
		searchType(type) {
			if (this.getResults(type).results.length) return
			try {
				this.fetchSearchResultsForType(type)
			} catch (e) {
				console.error(e)
				this.showError = true
			}
		},
		orderby(order) {
			this.sortOrder = order
		},
		sortOrder(order) {
			if (this.results.orderby === order) return
			this.clearResults()
			this.fetchSearchResultsDebounced()
		},
		user_ids(ids, old) {
			if (isEqual(ids, old)) return
			this.resultsByType = {}
			this.users = this.user_ids
			this.isLoading = true
			this.fetchSearchResultsDebounced()
		},
		bubble_ids(ids, old) {
			if (isEqual(ids, old)) return
			this.resultsByType = {}
			this.chats = this.bubble_ids
			this.isLoading = true
			this.fetchSearchResultsDebounced()
		},
		relative_date_duration(duration, old) {
			if (isEqual(duration, old)) return
			this.resultsByType = {}
			this.isLoading = true
			this.fetchSearchResultsDebounced()
		},
		start_date(start, old) {
			if (isEqual(start, old)) return
			this.resultsByType = {}
			this.isLoading = true
			this.fetchSearchResultsDebounced()
		},
		end_date(end, old) {
			if (isEqual(end, old)) return
			this.resultsByType = {}
			this.isLoading = true
			this.fetchSearchResultsDebounced()
		},
		buster(buster) {
			if (!buster) return
			const query = { ...this.$route.query }
			delete query._
			this.$router.replace({ query })
			this.clearResults()
			this.fetchSearchResultsDebounced()
		},
	},

	computed: {
		results() {
			return (
				this.resultsByType[this.searchType] || Object.assign({}, emptyState)
			)
		},

		selectedTab() {
			return tabs.find((t) => t.path === this.searchType)
		},

		otherResults() {
			return Object.values(this.resultsByType).filter((r) => r.count > 0)
		},

		hasFilters() {
			return (
				this.bubble_ids ||
				this.user_ids ||
				this.relative_date_duration ||
				this.start_date ||
				this.end_date
			)
		},

		hasSearchCriteria() {
			return !!(
				this.query ||
				this.bubble_ids ||
				this.user_ids ||
				this.relative_date_duration ||
				this.start_date ||
				this.end_date
			)
		},

		isEmpty() {
			return Object.values(this.resultsByType).every((r) => r.count === -1)
		},
	},

	methods: {
		getResults(type = this.searchType) {
			return this.resultsByType[type] || Object.assign({}, emptyState)
		},

		tabHasCount(tab) {
			return this.getResults(tab.path).count >= 0
		},

		closeSearch() {
			EventBus.$emit('clearSearchQuery')
			this.onClickClose()
		},

		onClickClose() {
			this.clearResults()
			if (this.backToRoute) {
				this.$router.replace(this.backToRoute)
				this.backToRoute = null
			} else {
				redirectToLastActiveGroup()
			}
		},

		onTabChange(tab) {
			this.goToTab(tab.path)
		},

		onFilterOpen(isOpen) {
			if (isOpen) return
			const { chats, users, dates = {} } = this
			setSearchQueryParams({ chats, users, ...dates }, true)
		},

		goToTab(tabPath) {
			const { params, query } = this.$route
			const tab = tabs.find((t) => t.path === tabPath)
			if (params.tab === tab.path) return
			this.$router.replace({
				name: 'search',
				params: { type: tab.path },
				query: { ...query, q: this.query },
			})
		},

		async fetchSearchResults() {
			// if we have no query params we should return out
			// there is probably a cleaner way to approach this
			if (!this.hasSearchCriteria) {
				this.showError = false
				this.isLoading = false
				return
			}

			// fetch first page of results for all search types
			try {
				this.isLoading = true
				const promises = Object.values(types).map((type) =>
					this.fetchSearchResultsForType(type)
				)
				await Promise.all(promises)
				this.isLoading = false
			} catch (e) {
				if (isCanceled(e)) return
				console.error(e)
				this.showError = true
				this.isLoading = false
			}
		},

		async loadNextPage() {
			const { nextPage, isComplete } = this.getResults()
			if (isComplete || !nextPage) return
			try {
				this.isLoading = true
				this.fetchSearchResultsForType(this.searchType, nextPage)
			} catch (e) {
				if (isCanceled(e)) return
				console.error(e)
				this.showError = true
			} finally {
				this.isLoading = false
			}
		},

		clearResults() {
			this.showError = false
			this.cancelTokensByType = {}
			this.resultsByType = Object.values(types).reduce((acc, type) => {
				acc[type] = Object.assign({}, emptyState)
				return acc
			}, {})
		},

		onSortChange(s) {
			if (s === this.orderby) return
			const { query } = this.$route
			this.$router.replace({
				name: 'search',
				params: { type: this.searchType },
				query: { ...query, s },
			})
		},

		async fetchSearchResultsForType(search_type, from = 0) {
			// check if this type of search is already complete
			const { results: prevResults, isComplete } = this.getResults(search_type)
			if (isComplete) return

			// cancel any previous requests for this search type
			this.cancelTokensByType[search_type]?.cancel()
			this.cancelTokensByType[search_type] = getCancelToken()

			const body = {
				search_type,
				size: PAGE_SIZE,
				from,
				orderby: this.sortOrder,
			}

			if (this.query) {
				body.query = this.query
			}

			// Add optional fields to the body
			if (this.user_ids?.length > 0) body.user_ids = this.user_ids
			if (this.bubble_ids?.length > 0) body.bubble_ids = this.bubble_ids

			if (this.start_date || this.end_date) {
				const dates = this.formatSearchDates()
				Object.assign(body, dates)
			}

			if (
				this.relative_date_duration &&
				this.relative_date_duration !== 'ANY'
			) {
				body.relative_date_duration = this.relative_date_duration
			}

			const {
				results,
				total_results,
				next_page_offset,
				bubbles,
			} = await searchMessages(body, this.cancelTokensByType[search_type])
			const normalized = results.map((r) => {
				r.message = normalizeMessage(r.message)
				const group = bubbles.find((g) => g.bubble_id === r.message.bubble_id)
				return { ...r, group }
			})
			this.resultsByType[search_type] = {
				query: this.query,
				orderby: this.orderby,
				type: search_type,
				results: prevResults.concat(normalized),
				count: total_results,
				isComplete: next_page_offset >= total_results,
				nextPage: next_page_offset,
			}
			if (search_type === types.MESSAGES) {
				if (body.start_date) {
					body.start_date = this.start_date
				}

				if (body.end_date) {
					body.end_date = this.end_date
				}
				saveRecentSearch(body)
			}
		},

		tryAgain() {
			this.clearResults()
			this.fetchSearchResults()
		},

		serializeSearchParams() {
			const paramKeys = [
				'query',
				'bubble_ids',
				'user_ids',
				'start_date',
				'end_date',
				'orderby',
			]
			return paramKeys.reduce((acc, key) => {
				const value = this[key]
				if (Array.isArray(value) ? value.length > 0 : value) {
					acc[key] = value
				}
				return acc
			}, {})
		},

		clearFilters() {
			let { chats, users, duration, sd, ed, ...query } = this.$route.query
			if (chats || users || duration || sd || ed) {
				this.$router.replace({ query })
			}
		},

		dateFilterUpdate(dates) {
			const { chats, users } = this
			setSearchQueryParams({ chats, users, ...dates }, true)
		},

		formatSearchDates() {
			const userTimezone = moment.tz.guess() // Guess the user's timezone

			if (this.start_date && this.end_date) {
				// Check for same day
				if (
					moment
						.tz(this.start_date, userTimezone)
						.isSame(moment.tz(this.end_date, userTimezone), 'day')
				) {
					// ON filter
					return {
						start_date: moment
							.tz(this.start_date, userTimezone)
							.startOf('day')
							.format('YYYY-MM-DD HH:mm:ss'),
						end_date: moment
							.tz(this.end_date, userTimezone)
							.endOf('day')
							.format('YYYY-MM-DD HH:mm:ss'),
					}
					// Example result:
					// { start_date: "2024-07-01 00:00:00", end_date: "2024-07-01 23:59:59" }
				} else {
					// RANGE filter
					return {
						start_date: moment
							.tz(this.start_date, userTimezone)
							.startOf('day')
							.format('YYYY-MM-DD HH:mm:ss'),
						end_date: moment
							.tz(this.end_date, userTimezone)
							.endOf('day')
							.format('YYYY-MM-DD HH:mm:ss'),
					}
					// Example result:
					// { start_date: "2024-07-01 00:00:00", end_date: "2024-07-31 23:59:59" }
				}
			} else if (this.end_date && !this.start_date) {
				// BEFORE filter
				return {
					end_date: moment
						.tz(this.end_date, userTimezone)
						.subtract(1, 'day')
						.endOf('day')
						.format('YYYY-MM-DD HH:mm:ss'),
				}
				// Example result:
				// { end_date: "2024-07-02 00:00:00" }
			} else if (this.start_date && !this.end_date) {
				// AFTER filter
				return {
					start_date: moment
						.tz(this.start_date, userTimezone)
						.add(1, 'day')
						.startOf('day')
						.format('YYYY-MM-DD HH:mm:ss'),
				}
				// Example result:
				// { start_date: "2024-07-02 00:00:00" }
			}
		},
	},

	async beforeRouteLeave(to, from, next) {
		// capture the current scroll position for later use
		if (from.name === 'search') {
			const { list } = this.$refs
			if (list?.$el.scrollTop && this.results.results.length) {
				this.scrollPosition = list.$el.scrollTop
			}
			if (to.name === 'chat' && to.query.centered) {
				try {
					// check to see if group is hidden, if so, make bubble.info call
					this.loadingMessageId = parseInt(to.query.centered)
					if (!getChat(to.params.id).id) {
						await fetchMyMembershipForChat(to.params.id)
					}
					await this.$nextTick()
					this.loadingMessageId = null
				} catch (error) {
					console.error(error)
					this.$alert.error(this.$t('common_api_error_save'))
					this.loadingMessageId = null
					return
				}
			} else {
				this.backToRoute = null
				this.clearResults()
			}
		}
		next()
	},

	beforeRouteEnter(to, from, next) {
		const org = getOrganization()
		// if search is not enabled, redirect to home screen
		if (org && !org.search_enabled) return next({ name: 'home' })
		next()
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

@media screen and (max-width: 800px) {
	.search-wrapper {
		left: 0 !important;
	}
}

.search-wrapper {
	height: calc(100vh - #{$search-bar-height});
	display: flex;
	flex-direction: column;
	background-color: white;
}

.search-heading {
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
	height: 60px;
	padding: 0 35px 0 31px;
	border-bottom: solid 1px #e2e4e6;
	flex-shrink: 0;
	font-size: 18px;
	color: var(--neutral-600);
	@media screen and (max-width: 800px) {
		padding-left: 80px;
	}
	.close-icon {
		font-size: 24px;
		color: #4f5053;
	}
}

.search-filters {
	@include flex-spread;
	height: 50px;
	padding: 0 31px;
	flex-shrink: 0;
	border-bottom: solid 1px var(--neutral-40040);
	.search-filters-right {
		@include flex(16px);
	}
	.filter-icon-container {
		display: flex;
		align-items: center;
		justify-content: center;
		border: 1px solid var(--neutral-200);
		border-radius: 50%;
		width: 32px;
		height: 32px;
		.filter-icon {
			color: var(--blue-300);
			font-size: 15px;
		}
	}
	.clear-filters {
		margin-left: auto;
	}
	.clear-filters-placeholder {
		width: 0;
		height: 40px;
		margin-left: auto;
	}
}

.search-results {
	flex-grow: 1;
}

.search-loading,
.search-no-results,
.search-error {
	flex-grow: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	width: 100%;
	color: #9498a3;
	font-family: 'Avenir-Medium';
	font-size: 14px;
	> svg {
		margin-bottom: 32px;
	}
}

.no-results-icon {
	height: 150px;
	width: 167px;
	background: url('/images/common/no-search-results.svg') no-repeat center;
}

.search-error-icon {
	height: 150px;
	width: 180px;
	background: url('/images/common/search-error.svg') no-repeat center;
}

.search-anchor {
	color: var(--blue-300);
	font-family: 'Avenir-Medium';
	font-size: 14px;
	margin-top: 8px;
	text-decoration: underline;
	cursor: pointer;
}

.search-query {
	font-family: Avenir-Heavy;
}

:deep(.tabs-container) {
	.tabs {
		padding-left: 20px;
		font-size: 18px;
		&.left.selected-bottom {
			padding: 0;
			margin-top: 8px !important;
		}

		.tab-item {
			font-size: 14px;
			text-transform: none;
			color: #4f5053;
			font-family: Avenir-Book;
			padding: 16px 32px;
			&.selected {
				color: var(--neutral-800);
				font-family: Avenir-Heavy;
				border-color: var(--teal-300);
			}
			&:hover {
				color: #000000;
			}
		}
	}
}

:deep(.spinner-div) {
	position: relative;
	margin-left: -8px;
}
</style>
<style lang="scss">
@import 'src/sass/variables';
.app-layout {
	&.announcement {
		.search-wrapper {
			height: calc(
				100vh - #{$search-bar-height} - #{$announcement-banner-height}
			);
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';
@include overrideScheme('dark') {
	.search-wrapper {
		background-color: var(--neutral-100);
		.search-heading {
			background-color: var(--neutral-0);
			border-bottom: solid 1px var(--neutral-700);
		}
		.search-heading,
		.tabs-container {
			border-bottom: solid 1px var(--neutral-700);
		}
		.search-loading {
			.spinner-div,
			.ios-spinner {
				background-color: var(--neutral-100) !important;
			}
		}

		.tabs-container {
			.tabs {
				.tab-item {
					color: var(--neutral-600);
					&.selected {
						color: var(--neutral-800);
					}
					&:hover {
						color: var(--neutral-800);
					}
				}
			}
		}

		.search-item-container {
			&:hover {
				background: var(--neutral-300);
			}
			background: var(--neutral-0);
			border: 1px solid var(--neutral-300);
			.message-result {
				.result-group,
				.hidden-group-icon,
				.chevron {
					color: var(--neutral-600);
				}
				.result-text,
				.poll-card {
					color: var(--neutral-800);
					.search-highlight {
						background: rgba(255, 187, 0, 0.35);
					}
				}
				.attachment-wrapper {
					border: 1px solid var(--neutral-300);
					background: var(--neutral-100);
					.attachment-title {
						color: var(--neutral-800);
						.search-highlight {
							background: rgba(255, 187, 0, 0.35);
						}
					}
					.attachment-size {
						color: var(--neutral-600);
					}
				}
			}

			.file-result {
				.media-title {
					color: var(--neutral-800);
					.search-highlight {
						background: rgba(255, 187, 0, 0.35);
					}
				}

				.media-container {
					.media-info {
						.media-details {
							.media-user,
							.media-time,
							.media-size {
								color: var(--neutral-600);
							}
						}
					}
				}

				.jump-to-group {
					color: var(--teal-300);
				}
			}
		}

		.search-no-results {
			.no-results-icon {
				background: url('/images/common/no-search-results-dark.svg') no-repeat
					center;
			}
		}

		.search-error {
			.search-error-icon {
				background: url('/images/common/search-error-dark.svg') no-repeat center;
			}
		}
	}
}
</style>
